/**
 * @file ventrata.api.js
 * @module VentrataAPI
 * @description API wrapper for Ventrata integrations (products, suppliers, bookings).
 * Methods return Axios promises from axiosInstance().
 */

import { axiosInstance } from './axios'
export const VentrataAPI = {

    /**
     * Get list of suppliers
     * @returns {Promise} Axios promise resolving to suppliers list
     */
    getSuppliers: () => {
        return axiosInstance().get("/ventrata/suppliers");
    },

    /**
     * Get supplier details by UUID
     * @param {string} uuid - Supplier UUID
     * @param {string|null} providerId - Optional provider id to scope the request
     * @returns {Promise} Axios promise resolving to supplier data
     */
    getSupplier: (uuid, providerId) => {
        return axiosInstance().get(`/ventrata/suppliers/${uuid}`, { params: { providerId: providerId } });
    },

    /**
     * Get products with optional query params
     * @param {Object} queries - Query parameters (pagination, filters)
     * @param {string|null} providerId - Optional provider id
     * @returns {Promise} Axios promise resolving to products
     */
    getProducts: (queries, providerId = null) => {
        return axiosInstance().get("/ventrata/products", { params: { providerId: providerId, ...queries } });
    },

    /**
     * Get product by UUID
     * @param {string} uuid - Product UUID
     * @param {boolean} [pickupRequested=true] - Whether pickup is required
     * @param {string} providerId - Optional provider id
     * @returns {Promise} Axios promise resolving to product details
     */
    getProduct: (uuid, pickupRequested = true, providerId) => {
        return axiosInstance().get(`/ventrata/products/${uuid}`, { params: { pickupRequested: pickupRequested, providerId: providerId } });
    },

    /**
     * Check availability for given payload
     * @param {Object} payload - Availability request payload
     * @param {string|null} providerId - Optional provider id
     * @returns {Promise} Axios promise resolving to availability results
     */
    availabilityCheck: (payload, providerId = null) => {
        return axiosInstance().post(`/ventrata/availability`, payload, { params: { providerId: providerId } });
    },

    /**
     * Create a reservation / booking in Ventrata
     * @param {Object} payload - Booking payload
     * @param {string|null} providerId - Optional provider id
     * @returns {Promise} Axios promise resolving to booking confirmation
     */
    createReservation: (payload, providerId = null) => {
        return axiosInstance().post(`/ventrata/bookings`, payload, { params: { providerId: providerId } });
    },

    /**
     * Update a reservation by id
     * @param {string} uuid - Booking UUID
     * @param {Object} payload - Patch payload
     * @param {string|null} providerId
     * @returns {Promise} Axios promise resolving to updated booking
     */
    updatedReservationById: (uuid, payload, providerId = null) => {
        return axiosInstance().patch(`/ventrata/bookings/${uuid}`, payload, { params: { providerId: providerId } });
    }
}